import React, { useState, useEffect } from 'react';
import { Wand2, AlertCircle, Loader2, ChevronDown, Sparkles, Settings, Key as KeyIcon, X } from 'lucide-react';
import { ImageUploader } from './components/ImageUploader';
import { PromptResult } from './components/PromptResult';
import { ApiKeyModal } from './components/ApiKeyModal';
import { analyzeImage, fileToBase64 } from './services/geminiService';
import { AppState, GeneratedResult } from './types';

const App: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [results, setResults] = useState<GeneratedResult[]>([]);
  const [appState, setAppState] = useState<AppState>(AppState.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [modelId, setModelId] = useState<string>('gemini-2.5-flash');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  
  // API Key State
  const [apiKeys, setApiKeys] = useState<string[]>([]);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [activeKeyDisplay, setActiveKeyDisplay] = useState<string | null>(null);
  
  // Notification State
  const [notification, setNotification] = useState<{message: string, type: 'warning' | 'error'} | null>(null);

  useEffect(() => {
    // Load Gemini Keys
    const storedKeys = localStorage.getItem('gemini_api_keys');
    if (storedKeys) {
      try {
        setApiKeys(JSON.parse(storedKeys));
      } catch (e) {
        console.error("Failed to parse stored keys", e);
      }
    } else {
        setIsKeyModalOpen(true);
    }
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleSaveKeys = (keys: string[]) => {
    setApiKeys(keys);
    localStorage.setItem('gemini_api_keys', JSON.stringify(keys));
  };

  const handleFilesSelect = (files: File[]) => {
    if (files.length > 10000) {
        setError("Maximum 10,000 files allowed.");
        return;
    }
    setSelectedFiles(files);
    setError(null);
    setAppState(AppState.IDLE);
    setResults([]); 
    setProgress({ current: 0, total: files.length });
  };

  const handleClearImages = () => {
    setSelectedFiles([]);
    setResults([]); 
    setAppState(AppState.IDLE);
    setError(null);
    setProgress({ current: 0, total: 0 });
  };

  const generatePrompts = async () => {
    if (selectedFiles.length === 0) return;
    
    // Initial check
    // Create a local copy of keys to manage rotation and removal during the loop
    let currentKeys = [...apiKeys]; 
    if (currentKeys.length === 0) {
        setIsKeyModalOpen(true);
        setError("Please configure your Google Gemini API Key first.");
        return;
    }

    setAppState(AppState.ANALYZING);
    setError(null);
    setResults([]);
    
    const total = selectedFiles.length;
    setProgress({ current: 0, total });

    let fileIndex = 0;
    let keyIndex = 0;

    // Use while loop to allow retrying the same file if a key fails
    while (fileIndex < total) {
      // Check if we ran out of keys mid-process
      if (currentKeys.length === 0) {
        setApiKeys([]); // Sync state
        setAppState(AppState.IDLE);
        setError("All API Keys have been exhausted (Rate Limit). Please add new keys.");
        setIsKeyModalOpen(true);
        setActiveKeyDisplay(null);
        return;
      }

      // Ensure keyIndex is valid
      if (keyIndex >= currentKeys.length) keyIndex = 0;

      const file = selectedFiles[fileIndex];
      const currentKey = currentKeys[keyIndex];
      
      // Update UI indicator
      setActiveKeyDisplay(currentKey);

      try {
        const base64 = await fileToBase64(file);
        const rawPrompt = await analyzeImage(currentKey, base64, modelId);
        
        const cleanPrompt = rawPrompt.replace(/[\r\n]+/g, ' ').trim();
        
        setResults(prev => [...prev, { 
          filename: file.name, 
          content: cleanPrompt,
          isError: false
        }]);

        // Success: Move to next file and rotate key
        fileIndex++;
        keyIndex++;
        setProgress({ current: fileIndex, total });

      } catch (err: any) {
        const errorMessage = err.message || '';
        const isRateLimit = 
            errorMessage.includes('429') || 
            errorMessage.includes('403') || 
            errorMessage.includes('quota') || 
            errorMessage.includes('exhausted') ||
            errorMessage.includes('limit');

        if (isRateLimit) {
            const deadKeySuffix = currentKey.slice(-4);
            console.warn(`Key ending in ...${deadKeySuffix} hit rate limit. Removing.`);
            
            // Remove the dead key from local array
            currentKeys.splice(keyIndex, 1);
            
            // Update State and LocalStorage immediately so UI reflects changes
            setApiKeys([...currentKeys]);
            localStorage.setItem('gemini_api_keys', JSON.stringify(currentKeys));

            // Show Notification
            setNotification({
                message: `Key ending in ...${deadKeySuffix} exhausted. Removed automatically. ${currentKeys.length} keys remaining.`,
                type: 'warning'
            });

            // Do NOT increment fileIndex. We retry the same file with the next key (which is now at the same index due to splice).
            // Do NOT increment keyIndex. 
            
        } else {
            // General error (e.g. invalid image data), not key related
            console.error(`Error processing ${file.name}:`, err);
            setResults(prev => [...prev, { 
                filename: file.name, 
                content: `[Error processing ${file.name}: ${errorMessage}]`,
                isError: true
            }]);
            
            // Move to next file
            fileIndex++;
            setProgress({ current: fileIndex, total });
        }
      }
    }

    setAppState(AppState.SUCCESS);
    setActiveKeyDisplay(null);
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E] text-slate-200 font-sans selection:bg-blue-500/30">
      
      {/* Toast Notification */}
      {notification && (
        <div className="fixed top-20 right-4 z-50 max-w-sm w-full animate-in slide-in-from-right fade-in duration-300">
            <div className={`p-4 rounded-lg shadow-2xl border flex items-start gap-3 backdrop-blur-md ${
                notification.type === 'warning' 
                    ? 'bg-orange-500/10 border-orange-500/20 text-orange-200' 
                    : 'bg-red-500/10 border-red-500/20 text-red-200'
            }`}>
                <AlertCircle className="shrink-0 mt-0.5" size={18} />
                <div className="flex-1 text-sm font-medium leading-tight">
                    {notification.message}
                </div>
                <button onClick={() => setNotification(null)} className="opacity-70 hover:opacity-100">
                    <X size={16} />
                </button>
            </div>
        </div>
      )}

      <ApiKeyModal 
        isOpen={isKeyModalOpen} 
        onClose={() => setIsKeyModalOpen(false)}
        onSave={handleSaveKeys}
        savedKeys={apiKeys}
      />

      {/* Header */}
      <header className="border-b border-white/5 bg-[#0B0C0E]/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg shadow-lg shadow-blue-500/20">
              <Sparkles size={18} className="text-white" />
            </div>
            <div className="flex flex-col">
              <h1 className="text-lg font-bold text-white tracking-tight leading-none">
                IMG to PROMPT
              </h1>
              <span className="text-[10px] text-slate-500 font-medium tracking-wide">
                Powered by Google Gemini
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
                onClick={() => setIsKeyModalOpen(true)}
                className={`
                    group flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border
                    ${apiKeys.length === 0 
                        ? 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20' 
                        : apiKeys.length === 1 && appState === AppState.ANALYZING
                             ? 'bg-orange-500/10 text-orange-300 border-orange-500/20 animate-pulse'
                             : 'bg-[#1A1B1E] text-slate-400 border-white/5 hover:border-white/10 hover:text-white'
                    }
                `}
            >
                {apiKeys.length === 0 ? <AlertCircle size={14} /> : <Settings size={14} className="group-hover:rotate-45 transition-transform duration-500" />}
                <span className="tabular-nums">
                    {apiKeys.length === 0 
                        ? "Set API Key" 
                        : (apiKeys.length > 1 ? `${apiKeys.length} Keys Ready` : "1 Key Ready")
                    }
                </span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-[calc(100vh-8rem)]">
          
          {/* Left Column: Input */}
          <div className="flex flex-col gap-6 h-full">
            <div className="bg-[#141517] rounded-xl p-1 border border-white/5 shadow-xl flex-shrink-0">
               <div className="p-5">
                   <h2 className="text-sm font-semibold mb-4 text-white flex items-center gap-2">
                       <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 text-[10px] text-white">1</span>
                       Upload Batch
                   </h2>
                   <ImageUploader 
                        onFilesSelect={handleFilesSelect}
                        selectedCount={selectedFiles.length}
                        onClear={handleClearImages}
                   />
               </div>
            </div>

            <div className="bg-[#141517] rounded-xl p-6 border border-white/5 shadow-xl flex-1 flex flex-col min-h-0">
              <h2 className="text-sm font-semibold mb-4 text-white flex items-center gap-2">
                   <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/10 text-[10px] text-white">2</span>
                   Configuration
               </h2>
              
              <div className="flex flex-col gap-6 h-full">
                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Gemini Model Selection</label>
                  <div className="relative group">
                    <select
                        value={modelId}
                        onChange={(e) => setModelId(e.target.value)}
                        className="w-full appearance-none bg-[#0B0C0E] border border-white/10 hover:border-white/20 text-sm text-white py-3 px-4 pr-10 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer"
                    >
                        <option value="gemini-2.5-flash">Gemini 2.5 Flash (Fast & Efficient)</option>
                        <option value="gemini-3-pro-preview">Gemini 3.0 Pro Preview (Highest Quality)</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none group-hover:text-slate-400 transition-colors" size={16} />
                  </div>
                </div>

                <div className="flex-1 flex flex-col justify-end gap-4">
                    {/* Progress and Active Key Indicator */}
                    {progress.total > 0 && appState === AppState.ANALYZING && (
                        <div className="space-y-3 bg-[#1A1B1E] p-3 rounded-lg border border-white/5">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-400">Processing...</span>
                                <span className="text-white font-mono">{Math.round((progress.current / progress.total) * 100)}%</span>
                            </div>
                            <div className="w-full bg-[#0B0C0E] rounded-full h-1.5 overflow-hidden">
                                <div 
                                    className="bg-blue-500 h-full rounded-full transition-all duration-300 shadow-[0_0_10px_rgba(59,130,246,0.5)]" 
                                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                                ></div>
                            </div>
                            
                            {activeKeyDisplay && (
                                <div className="flex items-center justify-between pt-1 border-t border-white/5 mt-2">
                                    <span className="text-[10px] text-slate-500 flex items-center gap-1.5">
                                        <KeyIcon size={10} />
                                        Active Key
                                    </span>
                                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                        ...{activeKeyDisplay.slice(-5)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg flex items-center gap-3 animate-in slide-in-from-bottom-2 fade-in">
                        <AlertCircle className="text-red-400 shrink-0" size={16} />
                        <p className="text-xs text-red-300">{error}</p>
                        </div>
                    )}

                    <button
                        onClick={generatePrompts}
                        disabled={selectedFiles.length === 0 || appState === AppState.ANALYZING}
                        className={`
                        w-full py-3.5 px-6 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200
                        ${selectedFiles.length === 0 || appState === AppState.ANALYZING
                            ? 'bg-[#1A1B1E] text-slate-600 border border-white/5 cursor-not-allowed' 
                            : 'bg-white text-black hover:bg-slate-200 shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_25px_rgba(255,255,255,0.2)] transform active:scale-[0.98]'
                        }
                        `}
                    >
                        {appState === AppState.ANALYZING ? (
                        <>
                            <Loader2 size={16} className="animate-spin" />
                            Processing {progress.current}/{progress.total}
                        </>
                        ) : (
                        <>
                            <Wand2 size={16} />
                            Start Batch
                        </>
                        )}
                    </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Output */}
          <div className="h-full bg-[#141517] rounded-xl border border-white/5 shadow-xl overflow-hidden flex flex-col">
             <PromptResult 
                 results={results} 
                 isProcessing={appState === AppState.ANALYZING}
                 progress={progress}
               />
          </div>

        </div>
      </main>
    </div>
  );
};

export default App;